import { Reveal, Eyebrow } from "./Shared";

const CONTRASTS = [
  ["One part of you wants freedom.", "Another part believes money must come through pressure, sacrifice, or constant work."],
  ["One part wants to be seen.", "Another part is still afraid of judgment."],
  ["One part has a powerful message.", "Another keeps watering it down so everybody will approve."],
];

const SIGNS = [
  "You want freedom but keep building obligations",
  "You desire more but feel guilty receiving it",
  "You change your message every week",
  "You create offers people do not immediately understand",
  "You keep chasing money without feeling connected to the work",
  "You know you are changing, but your business still represents the old you",
  "You have powerful ideas but no structure connecting them",
  "You keep starting over instead of building forward",
];

export default function Problem() {
  return (
    <section data-testid="problem-section" className="relative py-20 sm:py-32 bg-coal/40 border-y border-cream/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <Reveal>
          <Eyebrow>When the pieces don&rsquo;t match</Eyebrow>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-5 font-display font-extrabold uppercase tracking-tight leading-[0.95] text-3xl sm:text-5xl max-w-4xl">
            You can&rsquo;t build a clear business from a{" "}
            <span className="font-serif italic normal-case font-medium text-lime">divided identity.</span>
          </h2>
        </Reveal>

        <div className="mt-12 sm:mt-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            {CONTRASTS.map(([a, b], i) => (
              <Reveal key={a} delay={i * 0.08}>
                <div className="border-l-2 border-lime/60 pl-5">
                  <p className="text-cream font-medium text-base sm:text-lg">{a}</p>
                  <p className="mt-1.5 text-fog text-sm sm:text-base leading-relaxed">{b}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <p className="text-sm sm:text-base leading-relaxed text-fog">
                You do not necessarily need more information.{" "}
                <span className="text-cream">
                  You may need to bring your beliefs, identity, why, message, market, and offer
                  into the same direction.
                </span>
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <ul data-testid="problem-list" className="border-t border-cream/10">
              {SIGNS.map((sign, i) => (
                <Reveal key={sign} delay={i * 0.05} y={16}>
                  <li
                    data-testid={`problem-list-item-${i + 1}`}
                    className="group flex items-baseline gap-4 sm:gap-6 py-4 sm:py-5 border-b border-cream/10 transition-colors duration-300 hover:bg-cream/[0.03]"
                  >
                    <span className="font-mono text-[10px] sm:text-xs text-lime shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm sm:text-lg text-cream/85 group-hover:text-cream transition-colors duration-300 leading-snug">
                      {sign}
                    </span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>

        <Reveal delay={0.1}>
          <p className="mt-16 sm:mt-24 font-display font-extrabold uppercase tracking-tight leading-[0.95] text-2xl sm:text-4xl lg:text-5xl max-w-4xl">
            The plan cannot hold if the{" "}
            <span className="font-serif italic normal-case font-medium text-lime">person</span>{" "}
            building it doesn&rsquo;t believe in it.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
